import React, { useLayoutEffect, useRef, useState } from 'react';
import { SegmentedControlProps } from '../../../types/DesignSystem/SegmentedControl';
import { FOCUS_RING_INSET } from '../focusRing';
import { useMergedRef } from '../useMergedRef';
import { ControlSize } from '../../../types/DesignSystem/controlSize';
import { withClassName } from '../cn';
import { useControllableState } from '../useControllableState';

/*
 * § 297 — the segment's own height: the control rung minus the track's 2px inset on each
 * side, so the CONTROL lands on 28 / 36 / 44 (`CONTROL_PX`). Literals because the class
 * scanner cannot see a template. `sm` is what the control always rendered (p-0.5 + py-1 +
 * a 16px caption line = 28); the label now sits at `body-primary` inside the same box.
 */
const SEGMENT_H: Record<ControlSize, string> = {
  sm: 'tw-h-6',
  md: 'tw-h-8',
  lg: 'tw-h-10',
};

/**
 * SegmentedControl (§ 65) — supersedes the app's `ButtonSwitch` (a div-track of
 * buttons with no group semantics and colour-only selection). See the type file
 * for when to reach for it.
 *
 * Keyboard follows the radio-group contract: arrows both MOVE focus and SELECT,
 * wrapping at the ends and skipping disabled segments; the checked segment is
 * the group's single tab stop. `stopPropagation` on handled arrows matters when
 * this sits in a `Menu` footer — without it the menu's own list navigation
 * would steal the keystroke and yank focus back into the items.
 *
 * ## § 219 — the pill SLIDES
 *
 * The lifted pill used to be background classes on whichever button was checked,
 * so changing the value unmounted the pill from one element and painted it on
 * another — and a CSS transition animates a property on ONE element, so the
 * state change could only ever teleport. Same defect, same fix as `Tab`'s
 * indicator: ONE thumb owned by the group, measured from the checked segment
 * with `useLayoutEffect` (position correct before paint) and re-measured by a
 * `ResizeObserver` (container resizes and late webfonts move every segment
 * without changing a prop).
 *
 * Unlike Tab's 1px bar, the thumb has corners, so it cannot be `scaleX`-driven —
 * a radius under a 100x horizontal scale smears into a lens. It animates
 * `transform` + `width` inline instead; a segmented control is small and
 * switches rarely, so the width layout cost is irrelevant. Buttons are
 * `tw-relative` so they paint ABOVE the positioned thumb; the checked one keeps
 * carrying its state for hands and readers (`aria-checked`, ink, weight) while
 * the thumb carries only the chrome. In jsdom (no layout) the measurements stay
 * 0 and the thumb is a 0-width strip — the semantics never depended on it.
 */
const SegmentedControl = React.forwardRef<HTMLDivElement, SegmentedControlProps>(
  function SegmentedControl({
    options,
    value: valueProp,
    defaultValue,
    onChange,
    ariaLabel,
    'aria-label': ariaLabelNative,
    size = 'sm',
    fitContent = false,
    className = '',
    'data-testid': dataTestId,
    ...rest
  }, forwardedRef) {
    const containerRef = useRef<HTMLDivElement>(null);
    const setContainerRef = useMergedRef<HTMLDivElement>(containerRef, forwardedRef);
    const [thumb, setThumb] = useState({ left: 0, width: 0 });
    /* § 357 — uncontrolled only with `defaultValue` and no `value`. */
    const [value, setValue] = useControllableState<string | number | undefined>(valueProp, defaultValue, undefined);
    const select = (id: string | number) => {
      setValue(id);
      onChange?.(id);
    };

    useLayoutEffect(() => {
      const container = containerRef.current;
      if (!container) return undefined;

      const measure = () => {
        const checked = container.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]');
        const left = checked ? checked.offsetLeft : 0;
        const width = checked ? checked.offsetWidth : 0;
        /* Return `prev` when nothing moved — the Tab lesson: a ResizeObserver
           stream during a pane drag must not re-render on every frame. */
        setThumb((prev) => (prev.left === left && prev.width === width ? prev : { left, width }));
      };

      measure();

      if (typeof ResizeObserver === 'undefined') return undefined;
      const observer = new ResizeObserver(measure);
      observer.observe(container);
      /* § 338 — and each segment. A label too long for its even share widens its own
         segment (see `tw-whitespace-nowrap` below), so a segment can change width while the
         track does not — when the web font swaps in, or when the checked segment's
         `font-medium` makes its label wider — and the thumb would keep the stale width. */
      container.querySelectorAll<HTMLElement>('[role="radio"]').forEach((segment) => observer.observe(segment));
      return () => observer.disconnect();
      /* `options.length`, not `options` — the array is nearly always an inline
         literal at the call site (Tab's exact reasoning). */
    }, [value, options.length]);

    /*
      § 377 — ONE tab stop, always: the checked, enabled segment, else the first enabled one.
      Only the checked segment used to take `tabIndex` 0, and `value` is optional, so with
      nothing selected every segment was -1 and the control could not be reached from the
      keyboard at all (WCAG 2.1.1) — the radio-group rule is that the first one takes the stop.
    */
    const checkedIndex = options.findIndex((option) => option.id === value && !option.disabled);
    const rovingIndex = checkedIndex !== -1 ? checkedIndex : options.findIndex((option) => !option.disabled);

    const selectNeighbour = (event: React.KeyboardEvent<HTMLDivElement>, direction: 1 | -1) => {
      const radios = [...event.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]')];
      const count = options.length;
      /* § 377 — arrows move from the CHECKED segment, or from the FOCUSED one when nothing is
         checked (reachable now); with neither, from just outside the end they travel towards. */
      const currentIndex = options.findIndex((option) => option.id === value);
      const focusedIndex = radios.indexOf(event.currentTarget.ownerDocument.activeElement as HTMLElement);
      const base = currentIndex !== -1 ? currentIndex : focusedIndex;
      const start = base !== -1 ? base : (direction === 1 ? -1 : count);
      for (let step = 1; step <= count; step += 1) {
        const index = (((start + direction * step) % count) + count) % count;
        const candidate = options[index];
        if (candidate.id === value) return;
        if (!candidate.disabled) {
          select(candidate.id);
          radios[index]?.focus();
          return;
        }
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      const direction =
        event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1
        : null;
      if (direction === null) return;
      event.preventDefault();
      event.stopPropagation();
      selectNeighbour(event, direction);
    };

    return (
      <div
        {...rest}
        ref={setContainerRef}
        role="radiogroup"
        /* § 354 — the native spelling wins. It used to arrive in `rest` and be overwritten
           here, so `aria-label="View"` left the group unnamed. */
        aria-label={ariaLabelNative ?? ariaLabel}
        onKeyDown={handleKeyDown}
        data-testid={dataTestId}
        data-size={size}
        className={withClassName(`tw-relative tw-inline-flex ${fitContent ? '' : 'tw-w-full'} tw-rounded-ctl tw-bg-surface-sunken tw-p-0.5 tw-gap-0.5`, className)}
      >
        {/* The one thumb. First in DOM and positioned, so the relative buttons
            after it paint on top; hidden from AT — aria-checked on the segment
            is the fact, this is its shadow. `tw-top-0.5/-bottom-0.5` mirror the
            track's p-0.5 inset. */}
        <span
          aria-hidden="true"
          data-testid={dataTestId ? `${dataTestId}-thumb` : undefined}
          /* `forced-colors:tw-border` (§ 224): the thumb is a white fill and a
             shadow, and Windows High Contrast strips both — the border (its colour
             forced by the system) is what keeps the chosen segment marked there. */
          className="tw-absolute tw-top-0.5 tw-bottom-0.5 tw-left-0 tw-rounded-ctl tw-bg-surface tw-shadow-ds-sm forced-colors:tw-border motion-safe:tw-transition-all motion-safe:tw-duration-base motion-safe:tw-ease-swift"
          style={{ width: `${thumb.width}px`, transform: `translateX(${thumb.left}px)` }}
        />
        {options.map((option, index) => {
          const checked = option.id === value;
          return (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={index === rovingIndex ? 0 : -1}
              disabled={option.disabled}
              data-testid={option['data-testid'] ?? (dataTestId ? `${dataTestId}-option-${option.id}` : undefined)}
              onClick={() => { if (!option.disabled && !checked) select(option.id); }}
              /* `select-none` (§ 235): rapid segment toggling is exactly the gesture
                 that flashes a text selection. */
              /* § 297 — `body-primary`, the type every other control's label wears; this
                 was the one control set in a 12px caption beside 14px buttons and tabs. The
                 height comes from the rung, not from padding + a line box (controlSize.ts). */
              /* § 338 — `whitespace-nowrap`: the segment is a fixed height, so a label that
                 wraps overflows it — Product Cards' "Clamp 2 lines" broke onto two lines in the
                 320px rail and spilled out of the thumb. With wrapping off, `flex-1`'s
                 `min-width: auto` is the whole label, so a label longer than its even share
                 widens its own segment and the others split what is left. Labels that fit
                 their share lay out exactly as before. */
              className={`tw-relative ${fitContent ? '' : 'tw-flex-1'} tw-inline-flex tw-items-center tw-justify-center tw-px-3 ${SEGMENT_H[size]} tw-rounded-ctl tw-text-body-primary tw-text-center tw-whitespace-nowrap tw-select-none motion-safe:tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${FOCUS_RING_INSET} ${
                checked
                  /* Ink and weight stay ON the segment (§ 60's lesson — state
                     must not be colour-alone chrome); the ground and shadow
                     moved to the sliding thumb above. */
                  ? 'tw-text-ink tw-font-medium'
                  : option.disabled
                    ? 'tw-text-ink-disabled tw-cursor-not-allowed'
                    : 'tw-text-ink-secondary hover:tw-text-ink tw-cursor-pointer'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    );
  },
);

export default SegmentedControl;
